import { useState } from "react";
import { Button } from "@heroui/react";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";

import { Locale, LOCALES } from "../types";
import { useDocument, useUpdateDocument } from "../use-documents";

type Props = {
  slug: string;
};

// Редактор одного документа: переключатель языков + markdown-редактор с превью.
// Черновики хранятся по локали, чтобы не терять правки при переключении вкладок.
export function DocumentEditor({ slug }: Props) {
  const { data, isLoading } = useDocument(slug);
  const { mutateAsync, isPending } = useUpdateDocument();

  const [locale, setLocale] = useState<Locale>("ru");
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const serverContent = (loc: string) =>
    data.data.find((d) => d.locale === loc)?.content ?? "";

  const value = drafts[locale] ?? serverContent(locale);
  const dirty =
    drafts[locale] !== undefined && drafts[locale] !== serverContent(locale);

  const onChange = (next?: string) =>
    setDrafts((prev) => ({ ...prev, [locale]: next ?? "" }));

  const onSave = () =>
    mutateAsync({ slug, locale, content: value }).then(() =>
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[locale];
        return next;
      }),
    );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-row gap-2">
          {LOCALES.map((l) => (
            <Button
              key={l.key}
              size="sm"
              variant={l.key === locale ? "primary" : "ghost"}
              onPress={() => setLocale(l.key)}
            >
              {l.title}
            </Button>
          ))}
        </div>

        <Button isPending={isPending} isDisabled={!dirty} onPress={onSave}>
          Сохранить
        </Button>
      </div>

      <div data-color-mode="light">
        <MDEditor
          key={`${slug}-${locale}`}
          value={value}
          onChange={onChange}
          height={560}
          textareaProps={{ disabled: isLoading }}
        />
      </div>
    </div>
  );
}
